'use client';

import { useState, useEffect, useCallback } from 'react';
import { CONTENT_CONTAINER_SELECTOR, DEBOUNCE_MS } from './constants';
import { debounce } from '@/utils/debounce';

// DOM selectors (local to this hook)
const MAIN_HEADING_SELECTOR = 'h1'; // used to determine when sidebar becomes sticky

// layout constants (all values in pixels)
const MAX_SIDEBAR_WIDTH = 390; // ideal sidebar width when there's plenty of space
const MIN_SIDEBAR_WIDTH = 200; // below this, hide sidebar and show mobile view instead
const GAP_FROM_CONTAINER = 24; // horizontal gap between sidebar and main content
const MIN_EDGE_MARGIN = 16; // minimum space between sidebar and screen edge
const STICKY_TOP_OFFSET = 96; // distance from viewport top when sidebar becomes sticky

// font scaling: shrink text proportionally as sidebar gets narrower
const MIN_FONT_SCALE = 0.85; // smallest font scale (at MIN_SIDEBAR_WIDTH)
const MAX_FONT_SCALE = 1.0; // full size font scale (at MAX_SIDEBAR_WIDTH)

interface UseSidebarLayoutOptions {
  isMobile: boolean;
}

/**
 * Calculates and manages the floating sidebar layout on desktop.
 * The sidebar is positioned in the empty space to the left of the main content container.
 * It becomes sticky when the user scrolls past the main heading.
 */
export const useSidebarLayout = ({ isMobile }: UseSidebarLayoutOptions) => {
  const [sidebarLeft, setSidebarLeft] = useState<number | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState<number>(MAX_SIDEBAR_WIDTH);
  const [fontScale, setFontScale] = useState<number>(1);
  const [initialTop, setInitialTop] = useState<number | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  const stickyTop = STICKY_TOP_OFFSET;

  // calculate sidebar position and dimensions based on available space
  const calculatePosition = useCallback(() => {
    if (isMobile) return;

    const container = document.querySelector(CONTENT_CONTAINER_SELECTOR);
    if (container) {
      const containerRect = container.getBoundingClientRect();

      // calculate how much horizontal space we have to the left of the content
      // layout: [edge margin] [sidebar] [gap] [content container]
      const availableSpace = containerRect.left - GAP_FROM_CONTAINER - MIN_EDGE_MARGIN;

      if (availableSpace >= MIN_SIDEBAR_WIDTH) {
        // enough room for floating sidebar
        const width = Math.min(availableSpace, MAX_SIDEBAR_WIDTH);
        const leftPos = containerRect.left - width - GAP_FROM_CONTAINER;

        setSidebarWidth(width);
        setSidebarLeft(leftPos);

        // scale font size proportionally: narrower sidebar = smaller text
        // widthRatio is 0 at MIN_SIDEBAR_WIDTH, 1 at MAX_SIDEBAR_WIDTH
        const widthRatio = (width - MIN_SIDEBAR_WIDTH) / (MAX_SIDEBAR_WIDTH - MIN_SIDEBAR_WIDTH);
        const scale = MIN_FONT_SCALE + widthRatio * (MAX_FONT_SCALE - MIN_FONT_SCALE);
        setFontScale(scale);
      } else {
        // not enough room - hide floating sidebar (will fall back to mobile view)
        setSidebarLeft(null);
        setSidebarWidth(MAX_SIDEBAR_WIDTH);
        setFontScale(1);
      }
    }

    // track h1 position for sticky behavior threshold
    const mainHeading = document.querySelector(MAIN_HEADING_SELECTOR);
    if (mainHeading) {
      const headingRect = mainHeading.getBoundingClientRect();
      setInitialTop(headingRect.top + window.scrollY);
    }
  }, [isMobile]);

  // reset state when mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarLeft(null);
      setInitialTop(null);
      setSidebarWidth(MAX_SIDEBAR_WIDTH);
      setFontScale(1);
      setIsSticky(false);
    }
  }, [isMobile]);

  // recalculate position on resize and DOM changes (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const debouncedCalculatePosition = debounce(calculatePosition, DEBOUNCE_MS);

    calculatePosition(); // initial calculation on mount

    // recalculate when window is resized
    window.addEventListener('resize', debouncedCalculatePosition);

    // recalculate when DOM changes (e.g., content loads, banners appear/disappear)
    const observer = new MutationObserver(() => debouncedCalculatePosition());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', debouncedCalculatePosition);
      observer.disconnect();
    };
  }, [isMobile, calculatePosition]);

  // toggle sticky positioning based on scroll position
  useEffect(() => {
    // only apply sticky behavior when floating sidebar is active
    if (isMobile || sidebarLeft === null || initialTop === null) return;

    const handleScroll = () => {
      // become sticky when we've scrolled past where the h1 would meet the sticky offset
      const scrollThreshold = initialTop - stickyTop;
      setIsSticky(window.scrollY > scrollThreshold);
    };

    handleScroll(); // check initial state on mount
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, sidebarLeft, initialTop, stickyTop]);

  // sidebar is "floating" when we're on desktop AND there's enough space for it
  const isFloating = !isMobile && sidebarLeft !== null && initialTop !== null;

  return {
    sidebarLeft,
    sidebarWidth,
    fontScale,
    initialTop,
    stickyTop,
    isSticky,
    isFloating,
  };
};

export { MAX_SIDEBAR_WIDTH };
